/**
 * Which Facebook action_types count as what.
 *
 * These three sets ARE the definition of "a conversion", "a lead" and
 * "conversion revenue" for this product, and the reasoning below is the record
 * of decisions that have already been made and revisited. Out of the client so
 * that changing what a conversion MEANS is a change to a table somebody can
 * read, not an edit buried at line 72 of a 1,200-line API client.
 */

/**
 * Canonical Facebook conversion action types to SUM into `conversions`.
 *
 * Facebook reports the SAME conversion under several overlapping action_types —
 * e.g. `lead` ≡ `onsite_conversion.lead_grouped` ≡ `offsite_*_add_meta_leads`,
 * and `complete_registration` ≡ `omni_complete_registration` ≡
 * `offsite_conversion.fb_pixel_complete_registration`. Summing all of them
 * double/triple-counts, so we count ONLY the canonical top-level standard-event
 * names. Messaging conversations (`onsite_conversion.messaging_*`) are
 * intentionally EXCLUDED per a product decision (2026-07-22, standard-only).
 * Adjust this set to change what "a conversion" means for the dashboard.
 */
export const FB_CONVERSION_ACTION_TYPES = new Set<string>([
  'purchase',
  'lead',
  'complete_registration',
  'subscribe',
  'start_trial',
  'submit_application',
  'contact',
  'schedule',
  'donate',
])

/**
 * The lead-shaped subset of the basket above.
 *
 * A CPL contract is a promise about leads, and `conversions` counts purchases,
 * contacts, schedules and donations alongside them — so a cost per lead taken
 * from the total is understated, always in the agency's favour. These three are
 * the events this product counts as a lead, agreed with the account owner:
 * a lead form, a completed registration, a submitted application. Everything
 * else in FB_CONVERSION_ACTION_TYPES stays out.
 *
 * Deliberately a SUBSET, so lead_conversions <= conversions always holds — a
 * database CHECK enforces it.
 */
export const FB_LEAD_ACTION_TYPES = new Set<string>([
  'lead',
  'complete_registration',
  'submit_application',
])

/** action_types whose action_values sum to conversion revenue (purchase value). */
export const FB_VALUE_ACTION_TYPES = new Set<string>(['purchase', 'omni_purchase'])
